/*
 * El paso de fase, como una tarjeta que se completa.
 *
 * Antes esto era una lista de bloqueos en la jerga del dominio
 * ("platica_cerrada.6 · duro") y había que adivinar qué hacer con ella. Ahora cada
 * requisito dice qué falta y trae el atajo a la acción que lo resuelve; cuando
 * están todos, el botón se enciende con el nombre de la fase que viene:
 * «Avanzar a La Plática →».
 *
 * Los niveles del método se respetan tal cual: lo `absoluto` no se puede forzar,
 * lo `duro` sí pero con motivo y dejando huella, y lo `aviso` no estorba.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ErrorApi, sesion as yo } from "../../api/cliente";
import { useAplicarTransicion } from "../../api/hooks";
import { FASES, type Bloqueo, type Transicion } from "../../api/tipos";
import { useAvisos } from "../../shell/Aviso";
import { Campo, Modal } from "../../ui/Modal";
import { Num } from "../../ui/primitivas";

/** Lo que resuelve cada requisito, por el id que devuelve el dominio. */
export type AccionRequisito =
  | "sesion" | "persona" | "hallazgo" | "verificar" | "zona" | "gobernanza" | "artefacto"
  | "proceso" | "ficha" | "matriz" | "congelar" | "primer-encargo" | "compuerta"
  | "baseline" | "indicador" | "terminos" | "hoja";

const ATAJOS: Record<string, [AccionRequisito, string]> = {
  // D0 → D1 · La Plática
  "platica_cerrada.1": ["sesion", "Registrar La Plática"],
  "platica_cerrada.2": ["persona", "Agregar a quien firma"],
  "platica_cerrada.3": ["sesion", "Corregir la sesión"],
  "platica_cerrada.5": ["verificar", "Verificar hallazgos"],
  "platica_cerrada.6": ["zona", "Marcar zona"],
  "platica_cerrada.7": ["artefacto", "Generar la hoja"],
  // D1 → D2 · El Recorrido
  "recorrido_cerrado.1": ["sesion", "Registrar El Recorrido"],
  "recorrido_cerrado.2": ["proceso", "Registrar proceso"],
  "recorrido_cerrado.3": ["hallazgo", "Proponer observación"],
  "recorrido_cerrado.4": ["gobernanza", "Confirmar gobernanza"],
  "recorrido_cerrado.5": ["proceso", "Calcular el delta"],
  "recorrido_cerrado.6": ["proceso", "Medir tiempos"],
  // D2 → D3 · Las Voces
  "voces_cerradas.1": ["sesion", "Registrar Las Voces"],
  "voces_cerradas.2": ["persona", "Marcar frontline"],
  "voces_cerradas.3": ["hallazgo", "Proponer ansiedad"],
  "voces_cerradas.5": ["proceso", "Actualizar el delta"],
  // D2|D3 → D4 · Taller de Enfoque
  "cartera_priorizada.1": ["sesion", "Registrar el Taller"],
  "cartera_priorizada.2": ["ficha", "Agregar ficha"],
  "cartera_priorizada.3": ["ficha", "Anclar a evidencia"],
  "cartera_priorizada.4": ["matriz", "Acordar consenso"],
  "cartera_priorizada.5": ["matriz", "Puntuar"],
  "cartera_priorizada.6": ["congelar", "Congelar ranking"],
  "cartera_priorizada.7": ["sesion", "Escuchar al frontline"],
  // D4 → D5 · Cierre
  "cierre_habilitado.1": ["primer-encargo", "Abrir el primer encargo"],
  "cierre_habilitado.2": ["matriz", "Revisar la Matriz"],
  "cierre_habilitado.6": ["compuerta", "Validar compuertas"],
  "cierre_habilitado.7": ["compuerta", "Citar la métrica"],
  "cierre_habilitado.8": ["compuerta", "Citar la ansiedad"],
  // D5 → D6 · Propuesta
  "cierre_acordado.1": ["indicador", "Completar el indicador"],
  "cierre_acordado.2": ["baseline", "Completar el Exhibit 1"],
  "cierre_acordado.3": ["indicador", "Reapuntar el indicador"],
  "cierre_acordado.4": ["terminos", "Definir el fee-at-risk"],
  "cierre_acordado.5": ["terminos", "Agregar exclusiones"],
  "cierre_acordado.6": ["terminos", "Revisar disputas"],
  "cierre_acordado.7": ["terminos", "Registrar dependencias"],
  "cierre_acordado.8": ["baseline", "Adjuntar la captura"],
  "cierre_acordado.9": ["hoja", "Generar la hoja"],
};

/*
 * Lo mismo que pide el método, dicho como lo diría Pablo. El dominio habla de
 * `es_economic_buyer` y de `Artefacto "lo_que_escuchamos"`; en pantalla eso es
 * "quien firma" y "la hoja «Lo que escuchamos»". El detalle que manda el servidor
 * se conserva debajo, porque ahí está el número concreto que falta.
 */
const TEXTOS: Record<string, string> = {
  "platica_cerrada.1": "La Plática registrada y cerrada",
  "platica_cerrada.2": "Quien firma estuvo en La Plática",
  "platica_cerrada.3": "Consentimiento de grabación registrado, o por qué no se grabó",
  "platica_cerrada.4": "Se capturó después, sin laptops en la mesa",
  "platica_cerrada.5": "Al menos 3 hallazgos verificados de La Plática",
  "platica_cerrada.6": "Al menos 2 zonas calientes, cada una con evidencia verificada",
  "platica_cerrada.7": "La hoja «Lo que escuchamos», entregada",
  "platica_cerrada.8": "Perfil de la empresa completo",
  "platica_cerrada.9": "La Piedra entregada",
  "platica_cerrada.10": "Entre 5 y 9 macroprocesos en el perfil",
  "recorrido_cerrado.1": "El Recorrido registrado y cerrado",
  "recorrido_cerrado.2": "Cada zona finalista con su proceso observado, de 3 pasos o más",
  "recorrido_cerrado.3": "Cada zona finalista con una observación o métrica verificada",
  "recorrido_cerrado.4": "Gobernanza confirmada: quién firma y quién puede vetar",
  "recorrido_cerrado.5": "Al menos una zona con el delta confirmado",
  "recorrido_cerrado.6": "Tiempos medidos en al menos un paso de cada zona finalista",
  "recorrido_cerrado.7": "Entre 3 y 6 zonas finalistas",
  "voces_cerradas.1": "Tres conversaciones de Las Voces, cada una a solas",
  "voces_cerradas.2": "Las conversaciones fueron con gente de piso",
  "voces_cerradas.3": "Al menos una ansiedad verificada",
  "voces_cerradas.4": "Nada de Las Voces quedó atribuido sin permiso",
  "voces_cerradas.5": "El delta de alguna zona se actualizó con lo que dijo el piso",
  "voces_cerradas.6": "No más de 5 conversaciones",
  "cartera_priorizada.1": "El Taller de Enfoque registrado y cerrado",
  "cartera_priorizada.2": "Al menos 3 fichas de oportunidad",
  "cartera_priorizada.3": "Cada ficha anclada a un hallazgo verificado",
  "cartera_priorizada.4": "Cada ficha con su consenso acordado",
  "cartera_priorizada.5": "Puntuaron el dueño, alguien más del cliente y alguien de Nexlum",
  "cartera_priorizada.6": "El ranking está congelado",
  "cartera_priorizada.7": "Se escuchó a la gente de piso",
  "cartera_priorizada.8": "Si la dispersión es alta, queda una decisión que lo reconoce",
  "cartera_priorizada.9": "Entre 3 y 5 fichas",
  "cierre_habilitado.1": "Primer encargo abierto sobre la ficha #1",
  "cierre_habilitado.2": "La ficha #1 llega al umbral de 3.5",
  "cierre_habilitado.6": "Cada compuerta cita evidencia verificada y la validó Nexlum",
  "cierre_habilitado.7": "La compuerta de baseline cita una métrica candidata",
  "cierre_habilitado.8": "La compuerta de respeto cita una ansiedad, o lo que no se toca",
  "cierre_acordado.1": "El indicador tiene sus cinco componentes",
  "cierre_acordado.2": "Exhibit 1 completo y validado por el cliente",
  "cierre_acordado.3": "El indicador apunta a ese baseline",
  "cierre_acordado.4": "El fee-at-risk está definido",
  "cierre_acordado.5": "Al menos 5 exclusiones de atribución acordadas",
  "cierre_acordado.6": "La mecánica de disputas, con sus cuatro escalones",
  "cierre_acordado.7": "Dependencias del cliente registradas, o declarado que no hay",
  "cierre_acordado.8": "El Exhibit 1 trae captura de la fuente",
  "cierre_acordado.9": "La hoja de un solo número, generada",
};

const COMPUERTAS: [string, string][] = [
  ["g1_resultado_30d", "Compuerta 1 · Resultado visible en 30 días o menos"],
  ["g2_baseline_hoy", "Compuerta 2 · Baseline medible hoy, con fuente clara"],
  ["g3_respeta_lo_que_funciona", "Compuerta 3 · Respeta lo que funciona: no sustituye personas"],
];

function humano(b: Bloqueo): string {
  if (TEXTOS[b.id]) return TEXTOS[b.id];
  const compuerta = COMPUERTAS.find(([clave]) => b.descripcion.includes(clave));
  return compuerta ? compuerta[1] : b.descripcion;
}

/** Las compuertas se numeran en serie (cierre_habilitado.3, .4, .5): todas se validan igual. */
function atajoDe(id: string): [AccionRequisito, string] | null {
  if (ATAJOS[id]) return ATAJOS[id];
  if (id.startsWith("cierre_habilitado.")) return ["compuerta", "Validar compuerta"];
  return null;
}

const NOMBRE = (id: string) => FASES.find((f) => f.id === id)?.nombre ?? id;

export function PasoDeFase({ casoId, cuentaId, transicion, onAccion }: {
  casoId: string;
  cuentaId: string;
  transicion: Transicion | undefined;
  onAccion: (a: AccionRequisito) => void;
}) {
  const nav = useNavigate();
  const { avisar } = useAvisos();
  const aplicar = useAplicarTransicion(casoId, cuentaId);
  const [forzar, setForzar] = useState(false);
  const [motivos, setMotivos] = useState<Record<string, string>>({});
  const [error, setError] = useState("");

  if (!transicion) {
    return (
      <section className="tarjeta" style={{ borderColor: "var(--verified)" }}>
        <h3 className="seccion-h" style={{ marginBottom: 4 }}>El descubrimiento terminó</h3>
        <p style={{ fontSize: 13, color: "var(--muted)" }}>Ya no hay fase siguiente en esta máquina: lo que sigue vive en Propuestas.</p>
      </section>
    );
  }

  const t = transicion;
  const duros = t.bloqueos.filter((b) => b.nivel === "duro");
  const absolutos = t.bloqueos.filter((b) => b.nivel === "absoluto");
  const avisos = t.bloqueos.filter((b) => b.nivel === "aviso");
  const faltan = absolutos.length + duros.length;
  const destino = `${t.hacia} · ${NOMBRE(t.hacia)}`;

  const ir = (a: AccionRequisito) => (a === "verificar" ? nav(`/verificacion/${casoId}`) : onAccion(a));

  const ejecutar = async (overrides: { condicion_id: string; motivo: string }[] = []) => {
    setError("");
    try {
      await aplicar.mutateAsync({ hacia: t.hacia, actor_id: yo.quien(), overrides });
      setForzar(false);
      avisar(`Caso en ${destino}`);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : "No se pudo aplicar la transición.");
    }
  };

  const fila = (b: Bloqueo | null, texto: string, detalle?: string | null) => {
    const atajo = b ? atajoDe(b.id) : null;
    return (
      <div key={texto + (b?.id ?? "")} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "7px 0", borderTop: "1px solid var(--border)" }}>
        <span style={{ color: b ? (b.nivel === "absoluto" ? "var(--risk)" : b.nivel === "duro" ? "var(--open-text)" : "var(--faint)") : "var(--verified)", flexShrink: 0, fontSize: 13, lineHeight: 1.5 }}>
          {b ? "○" : "✓"}
        </span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.45, color: b ? "var(--text)" : "var(--muted)" }}>
          {texto}
          {detalle && <span style={{ display: "block", fontSize: 11.5, color: "var(--faint)" }}>{detalle}</span>}
        </span>
        {atajo && (
          <button className="btn btn-fantasma" style={{ padding: 0, fontSize: 11.5, flexShrink: 0 }} onClick={() => ir(atajo[0])}>{atajo[1]} →</button>
        )}
      </div>
    );
  };

  return (
    <section className="tarjeta" style={{ borderColor: t.permitida ? "var(--verified)" : "var(--border)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 2 }}>
        <Num n={FASES.findIndex((f) => f.id === t.hacia) + 1} activo={!t.permitida} hecho={t.permitida} />
        <h3 className="subtitulo" style={{ fontSize: 16 }}>Avanzar a {NOMBRE(t.hacia)}</h3>
        <span className="mono" style={{ color: "var(--faint)", fontSize: 11 }}>{t.desde} → {t.hacia}</span>
      </div>
      <p style={{ fontSize: 12.5, color: "var(--muted)", marginBottom: 6 }}>
        {faltan === 0 ? "Todo lo que el método pide está cumplido." : `Faltan ${faltan} requisito${faltan === 1 ? "" : "s"}.`}
      </p>

      {/* Lo cumplido primero, para que se vea el avance; luego lo que falta. */}
      {t.permitida && absolutos.length === 0 && duros.length === 0 && fila(null, "Los requisitos del método están cubiertos")}
      {absolutos.map((b) => fila(b, humano(b), b.detalle))}
      {duros.map((b) => fila(b, humano(b), b.detalle))}
      {avisos.length > 0 && (
        <p style={{ fontSize: 11.5, color: "var(--faint)", marginTop: 8, lineHeight: 1.5 }}>
          No bloquean: {avisos.map((a) => humano(a)).join(" · ")}
        </p>
      )}

      {error && <p style={{ fontSize: 12.5, color: "var(--risk)", marginTop: 8 }}>{error}</p>}

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
        <button className="btn btn-primario" disabled={!t.permitida || aplicar.isPending} onClick={() => ejecutar()}>
          Avanzar a {NOMBRE(t.hacia)} →
        </button>
        {!t.permitida && t.permitida_con_override && (
          <button className="btn btn-fantasma" style={{ fontSize: 12.5 }} onClick={() => setForzar(true)}>Forzar con motivo…</button>
        )}
        {!t.permitida && !t.permitida_con_override && absolutos.length > 0 && (
          <span style={{ fontSize: 11.5, color: "var(--faint)" }}>Lo que falta es absoluto: no se fuerza.</span>
        )}
      </div>

      {forzar && (
        <Modal titulo={`Forzar ${t.desde} → ${t.hacia}`} sub="Cada condición dura que se salta deja una Decisión visible para siempre en el expediente." onCerrar={() => setForzar(false)}
          pie={<>
            <button className="btn btn-secundario" onClick={() => setForzar(false)}>Cancelar</button>
            <button className="btn btn-navy" disabled={aplicar.isPending || duros.some((b) => !(motivos[b.id] ?? "").trim())}
              onClick={() => ejecutar(duros.map((b) => ({ condicion_id: b.id, motivo: (motivos[b.id] ?? "").trim() })))}>Forzar</button>
          </>}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {duros.map((b) => (
              <Campo key={b.id} label={b.descripcion}>
                <textarea value={motivos[b.id] ?? ""} onChange={(e) => setMotivos((m) => ({ ...m, [b.id]: e.target.value }))} placeholder="Por qué se avanza sin esto." />
              </Campo>
            ))}
            {error && <p style={{ fontSize: 12.5, color: "var(--risk)" }}>{error}</p>}
          </div>
        </Modal>
      )}
    </section>
  );
}
